import * as THREE from 'three';
import { getGroundHeight } from './maps.js';
import { resetVehiclePowerups, tickVehiclePowerups } from './powerups.js';
import { PROJECTILE_FIRE_CAPACITY, tickProjectileFireLimiter } from './projectileFireLimiter.js';

const UP = new THREE.Vector3(0, 1, 0);
const tempForward = new THREE.Vector3();
const tempRight = new THREE.Vector3();
const tempLocal = new THREE.Vector3();
const tempContactStart = new THREE.Vector3();
const tempContactEnd = new THREE.Vector3();
const tempContactMotion = new THREE.Vector3();
const tempContactRemote = new THREE.Vector3();
const tempQuaternion = new THREE.Quaternion();
const tempQuaternionB = new THREE.Quaternion();
const tempEuler = new THREE.Euler(0, 0, 0, 'YXZ');
const tempAngular = new THREE.Vector3();
const tempUp = new THREE.Vector3();
const tempColliderQuaternion = new THREE.Quaternion();
const tempColliderRight = new THREE.Vector3();
const tempColliderUp = new THREE.Vector3();
const tempColliderForward = new THREE.Vector3();
const tempColliderOffset = new THREE.Vector3();
const tempColliderForwardPlanar = new THREE.Vector3();
const tempColliderRightPlanar = new THREE.Vector3();
const tempSupportOffset = new THREE.Vector3();
const tempGravityTorque = new THREE.Vector3();
const tempGravityForce = new THREE.Vector3(0,-1,0);
const AIR_GRAVITY = 13.8;
const AIR_PITCH_SPEED = 2.35;
const AIR_YAW_SPEED = 2.55;
const AIR_ROLL_SPEED = 2.9;
const AIR_ROLL_TRIGGER_THRESHOLD = .35;
export const AIR_DODGE_WINDOW = .8;
const AIR_DODGE_DIRECTION_DEADZONE = .2;
const AIR_DODGE_PLANAR_IMPULSE = 6.2;
const AIR_DODGE_DIRECTIONAL_LIFT = 2.35;
const AIR_DODGE_NEUTRAL_LIFT = 3.6;
const AIR_DODGE_SPIN_SPEED = 11.5;
const AIR_DODGE_SPIN_DURATION = .62;
const AIR_DODGE_IMPACT_DURATION = .42;
const GAMEPAD_BUTTON_MAP = Object.freeze({
  jump:0, confirm:0, cancel:1, boost:1, handbrake:2,
  targetLock:3, cameraDistance:3,
  fireLeft:4, tabPrev:4, fireRight:5, tabNext:5,
  pause:9, stabilize:10, cameraReverse:11,
  powerup:12, up:12, down:13, left:14, right:15,
});
const GAMEPAD_DIRECTION_REPEAT_DELAY = 330;
const GAMEPAD_DIRECTION_REPEAT_INTERVAL = 115;
const DEFAULT_KEY_BINDINGS = Object.freeze({
  forward: ['KeyW', 'ArrowUp'],
  backward: ['KeyS', 'ArrowDown'],
  left: ['KeyA', 'ArrowLeft'],
  right: ['KeyD', 'ArrowRight'],
  jump: ['Space'],
  handbrake: ['KeyX'],
  boost: ['ShiftLeft', 'ShiftRight'],
  stabilize: ['KeyQ'],
  powerup: ['KeyE'],
  cameraDistance: ['KeyC'],
  testKo: ['KeyK'],
});

const clamp = THREE.MathUtils.clamp;
const dampFactor = (strength, dt) => 1 - Math.exp(-strength * dt);
const isEditableTarget = (target) => Boolean(
  target?.closest?.('input, textarea, select, [contenteditable="true"]'),
);

export class InputManager {
  constructor(target = window) {
    this.target=target;
    this.keys = new Set();
    this.pressed = new Set();
    this.gamepadPressed = new Set();
    this.gamepadHeld = new Set();
    this.gamepadDirections = { left:false,right:false,up:false,down:false };
    this.gamepadDirectionRepeatAt = { left:0,right:0,up:0,down:0 };
    this.gamepadActivity = false;
    this.activeGamepad = null;
    this.hapticsEnabled = true;
    this.hapticsStrength = 1;
    this.hapticPriority = 0;
    this.hapticUntil = 0;
    this.hapticLastPlayed = new Map();
    this.enabled = true;
    this.lastActiveDevice='keyboard-mouse';
    this.lockedInputDevice=null;
    this.matchInputActive=false;
    this.matchInputPaused=false;
    this.pointerElement=null;
    this.pointerLocked=false;
    // RMB habilita temporalmente una palanca virtual de cámara. Mientras se
    // mantiene, el eje horizontal también orienta el cañón; al soltarlo ambos
    // vuelven suavemente al frente. MMB invierte sólo la cámara.
    this.mouseAim={x:0,y:0};
    this.mousePressed=new Set();
    this.mouseHeld=new Set();
    this.mouseSensitivity=.42;
    this.invertMouseY=false;
    this.mouseButtons={fire:0,cameraReverse:1,cameraControl:2};
    this.bindings = Object.fromEntries(Object.entries(DEFAULT_KEY_BINDINGS).map(([action,codes])=>[
      action,[...codes],
    ]));
    this.refreshBindings();
    target.addEventListener('keydown', (event) => {
      if (isEditableTarget(event.target)) {
        this.keys.delete(event.code);
        this.pressed.delete(event.code);
        return;
      }
      if (!this.enabled) return;
      this.activateInputDevice('keyboard-mouse');
      if (this.blockedKeys.has(event.code)) event.preventDefault();
      if (!this.keys.has(event.code) && this.pulseKeys.has(event.code)) {
        this.pressed.add(event.code);
      }
      this.keys.add(event.code);
    });
    target.addEventListener('keyup', (event) => this.keys.delete(event.code));
    target.addEventListener('mousedown',(event)=>this.onMouseDown(event));
    target.addEventListener('mouseup',(event)=>this.onMouseUp(event));
    target.addEventListener('mousemove',(event)=>this.onMouseMove(event));
    target.addEventListener('wheel',(event)=>this.onMouseWheel(event),{passive:false});
    target.addEventListener('contextmenu',(event)=>{
      if(this.matchInputActive&&this.lockedInputDevice==='keyboard-mouse')event.preventDefault();
    });
    target.addEventListener('focusin', (event) => {
      if (isEditableTarget(event.target)) this.clear();
    });
    target.addEventListener('blur', () => { this.clear(); this.stopRumble(); });
    target.addEventListener('gamepadconnected', (event) => { this.activeGamepad = event.gamepad.index; });
    target.addEventListener('gamepaddisconnected', (event) => {
      this.stopRumble(event.gamepad);
      if (this.activeGamepad === event.gamepad.index) this.activeGamepad = null;
      if(this.matchInputActive&&this.lockedInputDevice==='gamepad'){
        this.activateInputDevice('keyboard-mouse');
      }
    });
    globalThis.document?.addEventListener?.('pointerlockchange',()=>{
      const wasLocked=this.pointerLocked;
      this.pointerLocked=globalThis.document?.pointerLockElement===this.pointerElement;
      if(wasLocked&&!this.pointerLocked&&this.matchInputActive&&!this.matchInputPaused
        &&this.lockedInputDevice==='keyboard-mouse'){
        this.mousePressed.add('pause');
      }
    });
  }

  setPointerElement(element){
    this.pointerElement=element||null;
  }

  setAimSettings({mouseSensitivity=42,invertMouseY=false,mouse={}}={}){
    this.mouseSensitivity=clamp(Number(mouseSensitivity)||0,0,100)/100;
    this.invertMouseY=Boolean(invertMouseY);
    this.mouseButtons={
      fire:Number.isInteger(mouse.fireButton)?mouse.fireButton:0,
      cameraReverse:Number.isInteger(mouse.cameraReverseButton)?mouse.cameraReverseButton:1,
      cameraControl:Number.isInteger(mouse.cameraControlButton)?mouse.cameraControlButton:2,
    };
  }

  emitInputDeviceChange(device){
    if(typeof globalThis.CustomEvent!=='function')return;
    try{
      this.target?.dispatchEvent?.(new CustomEvent('turbo-crown-input-device',{
        detail:{device,matchActive:this.matchInputActive},
      }));
    }catch{/* Entornos de prueba sin DOM completo. */}
  }

  activateInputDevice(device){
    const next=device==='gamepad'?'gamepad':'keyboard-mouse';
    const previous=this.lockedInputDevice||this.lastActiveDevice;
    this.lastActiveDevice=next;
    if(this.matchInputActive&&this.lockedInputDevice!==next){
      this.lockedInputDevice=next;
      this.mousePressed.clear();
      this.mouseHeld.clear();
      this.mouseAim.x=0;
      this.mouseAim.y=0;
      if(next==='gamepad'){
        this.keys.clear();
        this.pressed.clear();
        this.releasePointerCapture();
      }else{
        this.gamepadPressed.clear();
        this.requestPointerCapture();
      }
    }
    if(previous!==next)this.emitInputDeviceChange(next);
    return next;
  }

  noteGamepadActivity(pad){
    if(!pad)return;
    const analog=[...(pad.axes||[])].some((value)=>Math.abs(value||0)>.28)
      ||[...(pad.buttons||[])].some((button)=>button?.pressed||(button?.value||0)>.28);
    if(analog)this.activateInputDevice('gamepad');
  }

  beginMatchInput(device=this.lastActiveDevice){
    this.matchInputActive=true;
    this.matchInputPaused=false;
    this.lockedInputDevice=device==='gamepad'?'gamepad':'keyboard-mouse';
    this.lastActiveDevice=this.lockedInputDevice;
    this.mouseAim.x=0;
    this.mouseAim.y=0;
    this.mousePressed.clear();
    this.mouseHeld.clear();
    if(this.lockedInputDevice==='keyboard-mouse')this.requestPointerCapture();
    this.emitInputDeviceChange(this.lockedInputDevice);
    return this.lockedInputDevice;
  }

  endMatchInput(){
    this.matchInputActive=false;
    this.matchInputPaused=false;
    this.lockedInputDevice=null;
    this.mousePressed.clear();
    this.mouseHeld.clear();
    this.releasePointerCapture();
  }

  pauseMatchInput(paused=true){
    this.matchInputPaused=Boolean(paused);
    if(paused)this.releasePointerCapture();
    else if(this.lockedInputDevice==='keyboard-mouse')this.requestPointerCapture();
  }

  requestPointerCapture(){
    if(!this.matchInputActive||this.matchInputPaused||this.lockedInputDevice!=='keyboard-mouse')return false;
    try{
      const result=this.pointerElement?.requestPointerLock?.();
      result?.catch?.(()=>{});
      return Boolean(this.pointerElement);
    }catch{return false;}
  }

  releasePointerCapture(){
    if(globalThis.document?.pointerLockElement===this.pointerElement){
      try{globalThis.document.exitPointerLock?.();}catch{/* Browser sin Pointer Lock. */}
    }
  }

  onMouseDown(event){
    if(!this.enabled)return;
    this.activateInputDevice('keyboard-mouse');
    if(!this.matchInputActive||this.matchInputPaused||this.lockedInputDevice!=='keyboard-mouse')return;
    if(!this.pointerLocked){
      if(event.target===this.pointerElement)this.requestPointerCapture();
      return;
    }
    event.preventDefault();
    if(event.button===this.mouseButtons.fire)this.mousePressed.add('fire');
    if(event.button===this.mouseButtons.cameraControl){
      this.mouseAim.x=0;
      this.mouseAim.y=0;
      this.mouseHeld.add('cameraControl');
    }
    if(event.button===this.mouseButtons.cameraReverse)this.mouseHeld.add('cameraReverse');
  }

  onMouseUp(event){
    if(event.button===this.mouseButtons.cameraControl){
      this.mouseHeld.delete('cameraControl');
      this.mouseAim.x=0;
      this.mouseAim.y=0;
    }
    if(event.button===this.mouseButtons.cameraReverse)this.mouseHeld.delete('cameraReverse');
  }

  onMouseMove(event){
    if(!this.matchInputActive||this.matchInputPaused||!this.pointerLocked
      ||this.lockedInputDevice!=='keyboard-mouse')return;
    if(!this.mouseHeld.has('cameraControl'))return;
    const scale=.00055+this.mouseSensitivity*.00235;
    this.mouseAim.x=clamp(this.mouseAim.x+(event.movementX||0)*scale,-1,1);
    const vertical=(event.movementY||0)*scale*(this.invertMouseY?-1:1);
    this.mouseAim.y=clamp(this.mouseAim.y+vertical,-1,1);
  }

  onMouseWheel(event){
    if(!this.matchInputActive||this.matchInputPaused||this.lockedInputDevice!=='keyboard-mouse')return;
    event.preventDefault();
    this.mousePressed.add('cameraDistance');
  }

  currentGamepad() {
    const pads=navigator.getGamepads?.()||[];
    return (this.activeGamepad!==null?pads[this.activeGamepad]:null)
      ||Array.from(pads).find(Boolean)
      ||null;
  }

  hapticActuator(pad=this.currentGamepad()) {
    return pad?.vibrationActuator||pad?.hapticActuators?.[0]||null;
  }

  setHaptics({enabled=this.hapticsEnabled,strength=this.hapticsStrength}={}) {
    this.hapticsEnabled=Boolean(enabled);
    const numericStrength=Number(strength);
    this.hapticsStrength=Number.isFinite(numericStrength)
      ?clamp(numericStrength,0,1)
      :1;
    if(!this.hapticsEnabled||this.hapticsStrength<=0)this.stopRumble();
  }

  rumble({
    duration=120,
    weakMagnitude=.25,
    strongMagnitude=.25,
    startDelay=0,
    priority=1,
    key='default',
    minInterval=0,
  }={}) {
    if(!this.hapticsEnabled||this.hapticsStrength<=0)return false;
    const pad=this.currentGamepad();
    const actuator=this.hapticActuator(pad);
    if(!actuator)return false;
    const now=globalThis.performance?.now?.()??Date.now();
    const lastPlayed=this.hapticLastPlayed.get(key)||-Infinity;
    if(now-lastPlayed<Math.max(0,minInterval))return false;
    if(now<this.hapticUntil&&priority<this.hapticPriority)return false;
    const effectDuration=clamp(Number(duration)||0,0,5000);
    const scale=this.hapticsStrength;
    const weak=clamp((Number(weakMagnitude)||0)*scale,0,1);
    const strong=clamp((Number(strongMagnitude)||0)*scale,0,1);
    this.hapticLastPlayed.set(key,now);
    this.hapticPriority=priority;
    this.hapticUntil=now+Math.max(0,Number(startDelay)||0)+effectDuration;
    try{
      if(typeof actuator.playEffect==='function'){
        const result=actuator.playEffect('dual-rumble',{
          startDelay:Math.max(0,Number(startDelay)||0),
          duration:effectDuration,
          weakMagnitude:weak,
          strongMagnitude:strong,
        });
        result?.catch?.(()=>{});
        return true;
      }
      if(typeof actuator.pulse==='function'){
        const result=actuator.pulse(Math.max(weak,strong),effectDuration);
        result?.catch?.(()=>{});
        return true;
      }
    }catch{/* Algunos drivers anuncian háptica pero rechazan el efecto. */}
    return false;
  }

  stopRumble(pad=this.currentGamepad()) {
    this.hapticPriority=0;
    this.hapticUntil=0;
    const actuator=this.hapticActuator(pad);
    if(!actuator)return;
    try{
      if(typeof actuator.reset==='function'){
        const result=actuator.reset();
        result?.catch?.(()=>{});
      }else if(typeof actuator.playEffect==='function'){
        const result=actuator.playEffect('dual-rumble',{
          startDelay:0,duration:1,weakMagnitude:0,strongMagnitude:0,
        });
        result?.catch?.(()=>{});
      }else actuator.pulse?.(0,1)?.catch?.(()=>{});
    }catch{/* El mando puede haberse desconectado antes de detener el motor. */}
  }

  refreshBindings() {
    this.pulseKeys = new Set([
      ...this.bindings.jump,
      ...this.bindings.powerup,
      ...this.bindings.cameraDistance,
      ...this.bindings.testKo,
    ]);
    this.blockedKeys = new Set(Object.values(this.bindings).flat());
  }

  setBindings(bindings={}) {
    Object.keys(DEFAULT_KEY_BINDINGS).forEach((action)=>{
      const value=bindings[action];
      if(value===undefined)return;
      const codes=(Array.isArray(value)?value:[value]).filter((code)=>typeof code==='string'&&code);
      if(codes.length)this.bindings[action]=[...new Set(codes)];
    });
    this.refreshBindings();
    this.clear();
  }

  getBindings() {
    return Object.fromEntries(Object.entries(this.bindings).map(([action,codes])=>[action,[...codes]]));
  }

  has(action) {
    return this.bindings[action].some((key) => this.keys.has(key));
  }

  consume(action) {
    let found=false;
    this.bindings[action].forEach((key)=>{
      if(!this.pressed.has(key))return;
      this.pressed.delete(key);
      found=true;
    });
    return found;
  }

  deadzone(value, threshold = .16) {
    const absolute = Math.abs(value || 0);
    if (absolute <= threshold) return 0;
    return Math.sign(value) * (absolute-threshold)/(1-threshold);
  }

  pollGamepad() {
    const pads = navigator.getGamepads?.() || [];
    let pad = this.activeGamepad !== null ? pads[this.activeGamepad] : null;
    if (!pad) pad = Array.from(pads).find(Boolean) || null;
    if (!pad) {
      this.activeGamepad = null;
      this.gamepadHeld.clear();
      Object.keys(this.gamepadDirections).forEach((direction)=>{
        this.gamepadDirections[direction]=false;
        this.gamepadDirectionRepeatAt[direction]=0;
      });
      this.gamepadActivity=false;
      return null;
    }
    this.activeGamepad = pad.index;
    const nextHeld = new Set();
    Object.entries(GAMEPAD_BUTTON_MAP).forEach(([action,index]) => {
      if (pad.buttons[index]?.pressed || (pad.buttons[index]?.value || 0) > .55) {
        nextHeld.add(action);
        if (!this.gamepadHeld.has(action)) {
          this.gamepadPressed.add(action);
          this.gamepadActivity=true;
          if(!this.lockedInputDevice)this.lastActiveDevice='gamepad';
        }
      }
    });
    this.gamepadHeld = nextHeld;

    const axisX = this.deadzone(pad.axes[0] || 0,.2);
    const axisY = this.deadzone(pad.axes[1] || 0,.2);
    this.noteGamepadActivity(pad);
    const directionValues = {
      left: axisX < -.58 || nextHeld.has('left'),
      right: axisX > .58 || nextHeld.has('right'),
      up: axisY < -.58 || nextHeld.has('up'),
      down: axisY > .58 || nextHeld.has('down'),
    };
    const now=globalThis.performance?.now?.()??Date.now();
    Object.entries(directionValues).forEach(([direction,active]) => {
      if(active&&!this.gamepadDirections[direction]){
        this.gamepadPressed.add(`ui-${direction}`);
        this.gamepadDirectionRepeatAt[direction]=now+GAMEPAD_DIRECTION_REPEAT_DELAY;
        this.gamepadActivity=true;
        if(!this.lockedInputDevice)this.lastActiveDevice='gamepad';
      }else if(active&&now>=this.gamepadDirectionRepeatAt[direction]){
        this.gamepadPressed.add(`ui-${direction}`);
        this.gamepadDirectionRepeatAt[direction]=now+GAMEPAD_DIRECTION_REPEAT_INTERVAL;
        this.gamepadActivity=true;
      }else if(!active){
        this.gamepadDirectionRepeatAt[direction]=0;
      }
      this.gamepadDirections[direction] = active;
    });
    return pad;
  }

  consumeGamepad(action) {
    this.pollGamepad();
    if (!this.gamepadPressed.has(action)) return false;
    const buttonIndex=GAMEPAD_BUTTON_MAP[action];
    if(buttonIndex===undefined)this.gamepadPressed.delete(action);
    else Object.entries(GAMEPAD_BUTTON_MAP).forEach(([alias,index])=>{
      if(index===buttonIndex)this.gamepadPressed.delete(alias);
    });
    return true;
  }

  discardGamepadPulse(action) {
    const buttonIndex=GAMEPAD_BUTTON_MAP[action];
    if(buttonIndex===undefined){
      this.gamepadPressed.delete(action);
      return;
    }
    Object.entries(GAMEPAD_BUTTON_MAP).forEach(([alias,index])=>{
      if(index===buttonIndex)this.gamepadPressed.delete(alias);
    });
  }

  consumeGamepadActivity(){
    this.pollGamepad();
    const active=this.gamepadActivity;
    this.gamepadActivity=false;
    return active;
  }

  cameraStick() {
    const pad=this.pollGamepad();
    return {
      x:pad?this.deadzone(pad.axes[2]||0,.18):0,
      y:pad?this.deadzone(pad.axes[3]||0,.18):0,
      reverse:pad?this.gamepadHeld.has('cameraReverse'):false,
    };
  }

  snapshot() {
    const polledPad = this.pollGamepad();
    const activeDevice=this.lockedInputDevice||this.lastActiveDevice;
    const gamepadSelected=activeDevice==='gamepad';
    const useGamepad=gamepadSelected&&Boolean(polledPad);
    const useKeyboard=!gamepadSelected;
    const pad=useGamepad?polledPad:null;
    const axisX = pad ? this.deadzone(pad.axes[0] || 0) : 0;
    const axisY = pad ? this.deadzone(pad.axes[1] || 0) : 0;
    const rightTrigger = pad ? pad.buttons[7]?.value || 0 : 0;
    const leftTrigger = pad ? pad.buttons[6]?.value || 0 : 0;
    const gamepadThrottle = rightTrigger-leftTrigger;
    const keyboardThrottle = useKeyboard?((this.has('forward') ? 1 : 0) - (this.has('backward') ? 1 : 0)):0;
    const accelerator=Math.max(useKeyboard&&this.has('forward')?1:0,rightTrigger);
    const keyboardSteer = useKeyboard?((this.has('right') ? 1 : 0) - (this.has('left') ? 1 : 0)):0;
    const horizontalSteer = Math.abs(keyboardSteer) >= Math.abs(axisX) ? keyboardSteer : axisX;
    const gamepadAirRoll=useGamepad&&leftTrigger>AIR_ROLL_TRIGGER_THRESHOLD;
    const keyboardAirRoll=useKeyboard&&this.has('cameraDistance');
    const airRollActive=gamepadAirRoll||keyboardAirRoll;
    const airYaw=airRollActive
      ?0
      :Math.abs(keyboardSteer)>=Math.abs(axisX)?keyboardSteer:axisX;
    const keyboardJump=useKeyboard&&this.consume('jump');
    const gamepadJump=useGamepad&&this.consumeGamepad('jump');
    const keyboardFire=useKeyboard&&this.mousePressed.delete('fire');
    const gamepadFire=useGamepad&&(
      this.consumeGamepad('fireLeft')||this.consumeGamepad('fireRight')
    );
    const keyboardPowerup=useKeyboard&&this.consume('powerup');
    const gamepadPowerup=useGamepad&&this.consumeGamepad('powerup');
    if(gamepadPowerup)this.gamepadPressed.delete('ui-up');
    const mouseCameraActive=useKeyboard&&this.mouseHeld.has('cameraControl');
    const lookX=useKeyboard?(mouseCameraActive?this.mouseAim.x:0):(pad?this.deadzone(pad.axes[2]||0,.18):0);
    const lookY=useKeyboard?(mouseCameraActive?this.mouseAim.y:0):(pad?this.deadzone(pad.axes[3]||0,.18):0);
    // B/○ activa nitro en la partida, pero no debe quedar encolado como
    // cancelar al abrir luego una pantalla de pausa o resultados.
    if(useGamepad){
      this.discardGamepadPulse('cancel');
    }
    return {
      throttle: Math.abs(keyboardThrottle) >= Math.abs(gamepadThrottle) ? keyboardThrottle : gamepadThrottle,
      accelerator,
      steer: horizontalSteer,
      airPitch: keyboardThrottle !== 0 ? -keyboardThrottle : axisY,
      airYaw,
      airRoll:gamepadAirRoll?axisX:keyboardAirRoll?keyboardSteer:0,
      jump: keyboardJump || gamepadJump,
      boost:(useKeyboard&&this.has('boost'))||(useGamepad&&this.gamepadHeld.has('boost')),
      stabilize:(useKeyboard&&this.has('stabilize'))||(useGamepad&&this.gamepadHeld.has('stabilize')),
      handbrake: (useKeyboard&&this.has('handbrake')) || (useGamepad&&this.gamepadHeld.has('handbrake')),
      fire: keyboardFire || gamepadFire,
      powerup:keyboardPowerup||gamepadPowerup,
      targetLock:false,
      precisionAim:false,
      lookX,
      lookY,
      cameraReverse:useKeyboard
        ?this.mouseHeld.has('cameraReverse')
        :Boolean(pad&&this.gamepadHeld.has('cameraReverse')),
      testKo: useKeyboard&&this.consume('testKo'),
      pause:(useKeyboard&&this.mousePressed.delete('pause'))||(useGamepad&&this.consumeGamepad('pause')),
      inputDevice:gamepadSelected?'gamepad':'keyboard-mouse',
      pointerCaptured:gamepadSelected||this.pointerLocked,
    };
  }

  consumeCameraDistance({allowKeyboard=true}={}){
    if(this.lockedInputDevice==='keyboard-mouse'){
      const mouseRequested=this.mousePressed.delete('cameraDistance');
      const keyboardRequested=this.consume('cameraDistance');
      return mouseRequested||(allowKeyboard&&keyboardRequested);
    }
    return this.consumeGamepad('cameraDistance');
  }

  endFrame() {
    // La interfaz y la simulación consultan el mando desde bucles distintos.
    // Un pulso discreto permanece encolado hasta que su consumidor lo toma.
  }

  clear() {
    this.keys.clear();
    this.pressed.clear();
    this.gamepadPressed.clear();
    this.gamepadActivity=false;
    this.mousePressed.clear();
    this.mouseHeld.clear();
    this.mouseAim.x=0;
    this.mouseAim.y=0;
  }
}

function signedSpeed(velocity, yaw) {
  tempForward.set(-Math.sin(yaw), 0, -Math.cos(yaw));
  return velocity.dot(tempForward);
}

function resolveCircleAabb(position, radius, obstacle) {
  const closestX = clamp(position.x, obstacle.minX, obstacle.maxX);
  const closestZ = clamp(position.z, obstacle.minZ, obstacle.maxZ);
  let dx = position.x - closestX;
  let dz = position.z - closestZ;
  let distanceSq = dx * dx + dz * dz;
  if (distanceSq >= radius * radius) return null;

  if (distanceSq < .00001) {
    const toLeft = Math.abs(position.x - obstacle.minX);
    const toRight = Math.abs(obstacle.maxX - position.x);
    const toTop = Math.abs(position.z - obstacle.minZ);
    const toBottom = Math.abs(obstacle.maxZ - position.z);
    const minimum = Math.min(toLeft, toRight, toTop, toBottom);
    if (minimum === toLeft) { dx = -1; dz = 0; distanceSq = 1; }
    else if (minimum === toRight) { dx = 1; dz = 0; distanceSq = 1; }
    else if (minimum === toTop) { dx = 0; dz = -1; distanceSq = 1; }
    else { dx = 0; dz = 1; distanceSq = 1; }
  }
  const distance = Math.sqrt(distanceSq);
  return {
    normal: new THREE.Vector3(dx / distance, 0, dz / distance),
    depth: radius - distance,
    obstacle,
  };
}

function resolveCircleObstacle(position, radius, obstacle) {
  if (obstacle.shape !== 'circle') return resolveCircleAabb(position, radius, obstacle);
  let dx = position.x - obstacle.x;
  let dz = position.z - obstacle.z;
  let distanceSq = dx * dx + dz * dz;
  const minimumDistance = radius + obstacle.radius;
  if (distanceSq >= minimumDistance * minimumDistance) return null;
  if (distanceSq < .00001) {
    return {
      normal: new THREE.Vector3(1,0,0),
      depth: minimumDistance,
      obstacle,
    };
  }
  const distance = Math.sqrt(distanceSq);
  return {
    normal: new THREE.Vector3(dx / distance, 0, dz / distance),
    depth: minimumDistance - distance,
    obstacle,
  };
}

// Colisión continua en el plano XZ. Los colliders discretos alcanzan para
// velocidades normales, pero con nitro un auto puede recorrer de un tick al
// siguiente más que el espesor de una pared de mueble y aparecer del otro
// lado. La barrida encuentra la primera cara atravesada durante ese trayecto.
function sweepCircleAabb(previous, current, radius, obstacle) {
  const minX=obstacle.minX-radius;
  const maxX=obstacle.maxX+radius;
  const minZ=obstacle.minZ-radius;
  const maxZ=obstacle.maxZ+radius;
  if(previous.x>=minX&&previous.x<=maxX&&previous.z>=minZ&&previous.z<=maxZ)return null;

  const dx=current.x-previous.x;
  const dz=current.z-previous.z;
  let enter=0;
  let exit=1;
  let normalX=0;
  let normalZ=0;
  let hasNormal=false;

  if(Math.abs(dx)<.000001){
    if(previous.x<minX||previous.x>maxX)return null;
  }else{
    const near=dx>0?(minX-previous.x)/dx:(maxX-previous.x)/dx;
    const far=dx>0?(maxX-previous.x)/dx:(minX-previous.x)/dx;
    if(near>enter){
      enter=near;
      normalX=dx>0?-1:1;
      normalZ=0;
      hasNormal=true;
    }
    exit=Math.min(exit,far);
    if(enter>exit)return null;
  }

  if(Math.abs(dz)<.000001){
    if(previous.z<minZ||previous.z>maxZ)return null;
  }else{
    const near=dz>0?(minZ-previous.z)/dz:(maxZ-previous.z)/dz;
    const far=dz>0?(maxZ-previous.z)/dz:(minZ-previous.z)/dz;
    if(near>enter){
      enter=near;
      normalX=0;
      normalZ=dz>0?-1:1;
      hasNormal=true;
    }
    exit=Math.min(exit,far);
    if(enter>exit)return null;
  }

  if(!hasNormal||enter<0||enter>1||exit<0)return null;
  return {time:enter,normal:new THREE.Vector3(normalX,0,normalZ),obstacle};
}

function sweepCircleCircle(previous,current,radius,obstacle){
  const expandedRadius=radius+obstacle.radius;
  const startX=previous.x-obstacle.x;
  const startZ=previous.z-obstacle.z;
  if(startX*startX+startZ*startZ<=expandedRadius*expandedRadius)return null;
  const dx=current.x-previous.x;
  const dz=current.z-previous.z;
  const a=dx*dx+dz*dz;
  if(a<.000001)return null;
  const b=2*(startX*dx+startZ*dz);
  const c=startX*startX+startZ*startZ-expandedRadius*expandedRadius;
  const discriminant=b*b-4*a*c;
  if(discriminant<0)return null;
  const time=(-b-Math.sqrt(discriminant))/(2*a);
  if(time<0||time>1)return null;
  const normal=new THREE.Vector3(
    previous.x+dx*time-obstacle.x,
    0,
    previous.z+dz*time-obstacle.z,
  ).normalize();
  return {time,normal,obstacle};
}

function sweepCircleObstacle(previous,current,radius,collisionHeight,obstacle){
  const minimumBottom=Math.min(previous.y,current.y);
  const maximumTop=Math.max(previous.y,current.y)+collisionHeight;
  if(maximumTop<=obstacle.minY||minimumBottom>=obstacle.maxY)return null;
  if(Math.max(previous.x,current.x)+radius<obstacle.minX
    ||Math.min(previous.x,current.x)-radius>obstacle.maxX
    ||Math.max(previous.z,current.z)+radius<obstacle.minZ
    ||Math.min(previous.z,current.z)-radius>obstacle.maxZ)return null;
  const hit=obstacle.shape==='circle'
    ?sweepCircleCircle(previous,current,radius,obstacle)
    :sweepCircleAabb(previous,current,radius,obstacle);
  if(!hit)return null;
  const hitBottom=previous.y+(current.y-previous.y)*hit.time;
  const hitTop=hitBottom+collisionHeight;
  return hitTop>obstacle.minY&&hitBottom<obstacle.maxY?hit:null;
}

function obstacleContainsPointXZ(position, obstacle, margin = .08) {
  if (obstacle.shape === 'circle') {
    const dx = position.x - obstacle.x;
    const dz = position.z - obstacle.z;
    const radius = obstacle.radius + margin;
    return dx * dx + dz * dz <= radius * radius;
  }
  return position.x >= obstacle.minX - margin && position.x <= obstacle.maxX + margin
    && position.z >= obstacle.minZ - margin && position.z <= obstacle.maxZ + margin;
}

function finiteColliderValue(value,fallback){
  const number=Number(value);
  return Number.isFinite(number)?number:fallback;
}

export function vehicleColliderFor(
  vehicle,
  position=vehicle.car.position,
  yaw=null,
  orientationOverride=null,
){
  const data=vehicle.car.userData||{};
  const radius=Math.max(.05,finiteColliderValue(data.radius,.34));
  const width=Math.max(.05,finiteColliderValue(data.collisionWidth,radius*2));
  const length=Math.max(.05,finiteColliderValue(data.collisionLength,data.length||radius*2));
  const height=Math.max(.05,finiteColliderValue(data.collisionHeight,.82));
  const offsetX=finiteColliderValue(data.collisionOffsetX,0);
  const offsetY=finiteColliderValue(data.collisionOffsetY,height*.5);
  const offsetZ=finiteColliderValue(data.collisionOffsetZ,0);
  const orientation=orientationOverride?.isQuaternion
    ?tempColliderQuaternion.copy(orientationOverride).normalize()
    :Number.isFinite(yaw)
      ?tempColliderQuaternion.setFromAxisAngle(UP,yaw)
      :vehicle.orientation||vehicle.car.quaternion;
  const right=tempColliderRight.set(1,0,0).applyQuaternion(orientation);
  const up=tempColliderUp.set(0,1,0).applyQuaternion(orientation);
  const forward=tempColliderForward.set(0,0,1).applyQuaternion(orientation);
  const centerOffset=tempColliderOffset.set(offsetX,offsetY,offsetZ).applyQuaternion(orientation);
  const centerX=position.x+centerOffset.x;
  const centerY=position.y+centerOffset.y;
  const centerZ=position.z+centerOffset.z;
  const forwardPlanar=tempColliderForwardPlanar.set(forward.x,0,forward.z);
  if(forwardPlanar.lengthSq()<.0025){
    const fallbackYaw=Number.isFinite(yaw)?yaw:vehicle.yaw||0;
    forwardPlanar.set(Math.sin(fallbackYaw),0,Math.cos(fallbackYaw));
  }else forwardPlanar.normalize();
  const rightPlanar=tempColliderRightPlanar.set(forwardPlanar.z,0,-forwardPlanar.x);
  const halfWidth=width*.5;
  const halfLength=length*.5;
  const halfHeight=height*.5;
  const projectedHalfWidth=
    Math.abs(right.dot(rightPlanar))*halfWidth
    +Math.abs(forward.dot(rightPlanar))*halfLength
    +Math.abs(up.dot(rightPlanar))*halfHeight;
  const projectedHalfLength=
    Math.abs(right.dot(forwardPlanar))*halfWidth
    +Math.abs(forward.dot(forwardPlanar))*halfLength
    +Math.abs(up.dot(forwardPlanar))*halfHeight;
  const verticalHalfExtent=
    Math.abs(right.y)*halfWidth
    +Math.abs(forward.y)*halfLength
    +Math.abs(up.y)*halfHeight;
  return {
    centerX,
    centerY,
    centerZ,
    bottom:centerY-verticalHalfExtent,
    top:centerY+verticalHalfExtent,
    halfWidth:Math.max(.04,projectedHalfWidth),
    halfLength:Math.max(.04,projectedHalfLength),
    rightX:rightPlanar.x,
    rightZ:rightPlanar.z,
    forwardX:forwardPlanar.x,
    forwardZ:forwardPlanar.z,
  };
}

function projectedRadius(collider,axisX,axisZ){
  return collider.halfWidth*Math.abs(axisX*collider.rightX+axisZ*collider.rightZ)
    +collider.halfLength*Math.abs(axisX*collider.forwardX+axisZ*collider.forwardZ);
}

function colliderObstacleOverlapOnAxis(
  collider,
  halfWidth,
  halfLength,
  obstacleHalfX,
  obstacleHalfZ,
  deltaX,
  deltaZ,
  axisX,
  axisZ,
){
  const distance=Math.abs(deltaX*axisX+deltaZ*axisZ);
  const carRadius=halfWidth*Math.abs(axisX*collider.rightX+axisZ*collider.rightZ)
    +halfLength*Math.abs(axisX*collider.forwardX+axisZ*collider.forwardZ);
  const obstacleRadius=obstacleHalfX*Math.abs(axisX)+obstacleHalfZ*Math.abs(axisZ);
  return distance<=carRadius+obstacleRadius;
}

function colliderOverlapsObstacleXZ(collider,obstacle,margin=.015,footprintScale=.68){
  const scale=clamp(footprintScale,.1,1);
  const halfWidth=Math.max(.035,collider.halfWidth*scale);
  const halfLength=Math.max(.035,collider.halfLength*scale);
  if(obstacle.shape==='circle'){
    const dx=obstacle.x-collider.centerX;
    const dz=obstacle.z-collider.centerZ;
    const localX=dx*collider.rightX+dz*collider.rightZ;
    const localZ=dx*collider.forwardX+dz*collider.forwardZ;
    const closestX=clamp(localX,-halfWidth,halfWidth);
    const closestZ=clamp(localZ,-halfLength,halfLength);
    const outsideX=localX-closestX;
    const outsideZ=localZ-closestZ;
    const radius=Math.max(0,obstacle.radius)+margin;
    return outsideX*outsideX+outsideZ*outsideZ<=radius*radius;
  }

  const obstacleCenterX=(obstacle.minX+obstacle.maxX)*.5;
  const obstacleCenterZ=(obstacle.minZ+obstacle.maxZ)*.5;
  const obstacleHalfX=(obstacle.maxX-obstacle.minX)*.5+margin;
  const obstacleHalfZ=(obstacle.maxZ-obstacle.minZ)*.5+margin;
  const deltaX=obstacleCenterX-collider.centerX;
  const deltaZ=obstacleCenterZ-collider.centerZ;
  return colliderObstacleOverlapOnAxis(
    collider,halfWidth,halfLength,obstacleHalfX,obstacleHalfZ,deltaX,deltaZ,1,0,
  )&&colliderObstacleOverlapOnAxis(
    collider,halfWidth,halfLength,obstacleHalfX,obstacleHalfZ,deltaX,deltaZ,0,1,
  )&&colliderObstacleOverlapOnAxis(
    collider,halfWidth,halfLength,obstacleHalfX,obstacleHalfZ,deltaX,deltaZ,
    collider.rightX,collider.rightZ,
  )&&colliderObstacleOverlapOnAxis(
    collider,halfWidth,halfLength,obstacleHalfX,obstacleHalfZ,deltaX,deltaZ,
    collider.forwardX,collider.forwardZ,
  );
}

// Contacto entre los rectángulos orientados de dos autos. A diferencia del
// círculo histórico, este volumen respeta largo, ancho y offset calibrados.
export function vehicleColliderContact(
  a,
  b,
  positionA=a.car.position,
  positionB=b.car.position,
  yawA=null,
  yawB=null,
  padding=0,
){
  const first=vehicleColliderFor(a,positionA,yawA);
  const second=vehicleColliderFor(b,positionB,yawB);
  if(first.top<=second.bottom||first.bottom>=second.top)return null;
  const deltaX=second.centerX-first.centerX;
  const deltaZ=second.centerZ-first.centerZ;
  const axes=[
    [first.rightX,first.rightZ],
    [first.forwardX,first.forwardZ],
    [second.rightX,second.rightZ],
    [second.forwardX,second.forwardZ],
  ];
  let minimumOverlap=Infinity;
  let normalX=0;
  let normalZ=1;
  for(const [axisX,axisZ] of axes){
    const signedDistance=deltaX*axisX+deltaZ*axisZ;
    const overlap=projectedRadius(first,axisX,axisZ)
      +projectedRadius(second,axisX,axisZ)
      +Math.max(0,padding)
      -Math.abs(signedDistance);
    if(overlap<=0)return null;
    if(overlap<minimumOverlap){
      minimumOverlap=overlap;
      const direction=signedDistance<0?-1:1;
      normalX=axisX*direction;
      normalZ=axisZ*direction;
    }
  }
  return {
    first,
    second,
    overlap:minimumOverlap,
    normal:new THREE.Vector3(normalX,0,normalZ),
    position:new THREE.Vector3(
      (first.centerX+second.centerX)*.5,
      (Math.max(first.bottom,second.bottom)+Math.min(first.top,second.top))*.5,
      (first.centerZ+second.centerZ)*.5,
    ),
  };
}

export class ArcadeVehicle {
  constructor(car, world, options = {}) {
    this.car = car;
    this.world = world;
    this.config = car.userData.config;
    this.velocity = new THREE.Vector3();
    this.yaw = options.yaw ?? 0;
    this.pitch = 0;
    this.roll = 0;
    this.orientation = new THREE.Quaternion().setFromAxisAngle(UP,this.yaw);
    this.stepStartOrientation = this.orientation.clone();
    this.angularVelocity = new THREE.Vector3();
    this.bodyContactTipAxis = new THREE.Vector3();
    this.grounded = true;
    this.wheelsGrounded = true;
    this.uprightness = 1;
    this.groundHeight = 0;
    this.nitro = options.nitro ?? 100;
    this.health = 100;
    this.forwardSpeed = 0;
    this.lateralSpeed = 0;
    this.skidAmount = 0;
    this.distance = 0;
    this.steer = 0;
    this.throttle = 0;
    this.boosting = false;
    this.handbraking = false;
    this.collisionGhostTimer = 0;
    this.topSpeedMultiplier = this.config.cruiseTopSpeedMultiplier ?? 1.16;
    this.airTime = 0;
    this.lastAirTime = 0;
    this.impactCooldown = 0;
    this.projectileFireTokens = PROJECTILE_FIRE_CAPACITY;
    this.projectileFireRecharge = 0;
    this.impactPitchVelocity = 0;
    this.impactYawVelocity = 0;
    this.impactRollVelocity = 0;
    this.jumpBufferTimer = 0;
    this.groundedGraceTimer = 0;
    this.airDodgeAvailable = false;
    this.airDodgeWindowTimer = 0;
    this.airDodgeSpinTimer = 0;
    this.airDodgeImpactTimer = 0;
    this.disabled = false;
    this.respawnDelay = options.respawnDelay ?? 3;
    this.respawnTimer = 0;
    this.respawnProtection = options.respawnProtection ?? 0;
    this.invulnerableTimer = 0;
    this.getRespawn = options.getRespawn || null;
    this.spawnYaw = options.yaw ?? world.spawnYaw ?? 0;
    this.isPlayer = options.isPlayer ?? false;
    this.lastDamager = null;
    this.damageContributors = new Map();
    resetVehiclePowerups(this);
    this.onImpact = options.onImpact || (() => {});
    this.onLand = options.onLand || (() => {});
    this.onJump = options.onJump || (() => {});
    this.onDodge = options.onDodge || (() => {});
    this.onDestroyed = options.onDestroyed || (() => {});
    this.spawn = (options.position || world.spawn).clone();
    car.position.copy(this.spawn);
    car.rotation.order = 'YXZ';
    car.quaternion.copy(this.orientation);
  }

  reset(position = this.spawn, yaw = this.spawnYaw, withProtection = false) {
    this.car.position.copy(position);
    this.car.visible = true;
    this.velocity.set(0, 0, 0);
    this.yaw = yaw;
    this.pitch = 0;
    this.roll = 0;
    this.orientation.setFromAxisAngle(UP,this.yaw);
    this.stepStartOrientation.copy(this.orientation);
    this.angularVelocity.set(0,0,0);
    this.bodyContactTipAxis.set(0,0,0);
    this.grounded = false;
    this.wheelsGrounded = false;
    this.uprightness = 1;
    this.nitro = 100;
    this.health = 100;
    this.forwardSpeed = 0;
    this.lateralSpeed = 0;
    this.skidAmount = 0;
    this.steer = 0;
    this.throttle = 0;
    this.boosting = false;
    this.handbraking = false;
    this.collisionGhostTimer = 0;
    this.topSpeedMultiplier = this.config.cruiseTopSpeedMultiplier ?? 1.16;
    this.airTime = 0;
    this.impactCooldown = 0;
    this.projectileFireTokens = PROJECTILE_FIRE_CAPACITY;
    this.projectileFireRecharge = 0;
    this.impactPitchVelocity = 0;
    this.impactYawVelocity = 0;
    this.impactRollVelocity = 0;
    this.jumpBufferTimer = 0;
    this.groundedGraceTimer = 0;
    this.airDodgeAvailable = false;
    this.airDodgeWindowTimer = 0;
    this.airDodgeSpinTimer = 0;
    this.airDodgeImpactTimer = 0;
    this.disabled = false;
    this.respawnTimer = 0;
    this.invulnerableTimer = withProtection ? this.respawnProtection : 0;
    this.lastDamager = null;
    this.damageContributors.clear();
    resetVehiclePowerups(this);
    this.car.quaternion.copy(this.orientation);
  }

  resetAirDodge(){
    this.airDodgeAvailable=false;
    this.airDodgeWindowTimer=0;
    this.airDodgeSpinTimer=0;
    this.airDodgeImpactTimer=0;
  }

  armAirDodge(){
    this.airDodgeAvailable=true;
    this.airDodgeWindowTimer=AIR_DODGE_WINDOW;
  }

  performAirDodge(controls={}){
    if(!this.airDodgeAvailable||this.airDodgeWindowTimer<=0)return false;
    let right=clamp(Number(controls.steer)||0,-1,1);
    let forward=clamp(-(Number(controls.airPitch)||0),-1,1);
    const magnitude=Math.hypot(right,forward);
    const directional=magnitude>=AIR_DODGE_DIRECTION_DEADZONE;
    const reachScale=this.gameMode==='rupture'?.82:1;
    if(directional){
      const normalization=magnitude>1?1/magnitude:1;
      right*=normalization;
      forward*=normalization;
      tempLocal.copy(tempForward).multiplyScalar(forward)
        .addScaledVector(tempRight,right)
        .normalize();
      const massScale=1/Math.sqrt(this.config.mass*.9);
      this.velocity.addScaledVector(tempLocal,AIR_DODGE_PLANAR_IMPULSE*massScale*reachScale);
      this.velocity.y=Math.max(this.velocity.y,AIR_DODGE_DIRECTIONAL_LIFT*massScale*reachScale);
      this.angularVelocity.set(
        -forward*AIR_DODGE_SPIN_SPEED,
        this.angularVelocity.y*.25,
        -right*AIR_DODGE_SPIN_SPEED,
      );
      this.airDodgeSpinTimer=AIR_DODGE_SPIN_DURATION;
      this.airDodgeImpactTimer=AIR_DODGE_IMPACT_DURATION;
    }else{
      const lift=AIR_DODGE_NEUTRAL_LIFT/Math.sqrt(this.config.mass*.9)*reachScale;
      this.velocity.y=Math.min(9.6,Math.max(0,this.velocity.y)+lift);
      this.airDodgeSpinTimer=0;
      this.airDodgeImpactTimer=0;
    }
    this.airDodgeAvailable=false;
    this.airDodgeWindowTimer=0;
    this.jumpBufferTimer=0;
    this.onDodge({directional,forward,right});
    return true;
  }

  setOrientationQuaternion(quaternion){
    this.orientation.copy(quaternion).normalize();
    this.syncOrientationFields();
  }

  setOrientationEuler(pitch=this.pitch,yaw=this.yaw,roll=this.roll){
    tempEuler.set(pitch,yaw,roll,'YXZ');
    this.orientation.setFromEuler(tempEuler).normalize();
    this.syncOrientationFields();
  }

  syncOrientationFields(){
    tempEuler.setFromQuaternion(this.orientation,'YXZ');
    this.pitch=tempEuler.x;
    this.roll=tempEuler.z;
    tempForward.set(0,0,-1).applyQuaternion(this.orientation);
    const planarLength=Math.hypot(tempForward.x,tempForward.z);
    if(planarLength>.035)this.yaw=Math.atan2(-tempForward.x,-tempForward.z);
    tempUp.set(0,1,0).applyQuaternion(this.orientation);
    this.uprightness=clamp(tempUp.y,-1,1);
    this.car.quaternion.copy(this.orientation);
  }

  integrateLocalRotation(localAngularVelocity,dt){
    const angle=localAngularVelocity.length()*dt;
    if(angle<.000001)return;
    tempQuaternion.setFromAxisAngle(
      tempLocal.copy(localAngularVelocity).normalize(),
      angle,
    );
    this.orientation.multiply(tempQuaternion).normalize();
  }

  airRecoveryError(){
    tempQuaternion.copy(this.orientation).invert();
    const worldUpLocal=tempUp.copy(UP).applyQuaternion(tempQuaternion);
    return {
      pitch:Math.atan2(worldUpLocal.z,worldUpLocal.y),
      roll:Math.atan2(worldUpLocal.x,worldUpLocal.y),
    };
  }

  applyBodyContactGravity(dt){
    if(!this.grounded||this.wheelsGrounded){
      this.bodyContactTipAxis.set(0,0,0);
      return;
    }
    if(this.uprightness<-.78)this.bodyContactTipAxis.set(0,0,0);
    const data=this.car.userData||{};
    const halfWidth=Math.max(.025,finiteColliderValue(data.collisionWidth,.6)*.5);
    const halfLength=Math.max(.025,finiteColliderValue(data.collisionLength,data.length||1.05)*.5);
    const halfHeight=Math.max(.025,finiteColliderValue(data.collisionHeight,.82)*.5);
    const right=tempColliderRight.set(1,0,0).applyQuaternion(this.orientation);
    const up=tempColliderUp.set(0,1,0).applyQuaternion(this.orientation);
    const forward=tempColliderForward.set(0,0,1).applyQuaternion(this.orientation);
    tempSupportOffset.set(0,0,0);
    if(Math.abs(right.y)>.015)tempSupportOffset.addScaledVector(right,-Math.sign(right.y)*halfWidth);
    if(Math.abs(up.y)>.015)tempSupportOffset.addScaledVector(up,-Math.sign(up.y)*halfHeight);
    if(Math.abs(forward.y)>.015)tempSupportOffset.addScaledVector(forward,-Math.sign(forward.y)*halfLength);

    // El peso actúa en el centro de masa y genera torque alrededor del punto
    // más bajo del collider. Así un auto apoyado de trompa o sobre un lateral
    // deja de comportarse como si estuviera pegado al piso.
    tempGravityTorque
      .crossVectors(tempLocal.copy(tempSupportOffset).negate(),tempGravityForce)
      .applyQuaternion(tempQuaternion.copy(this.orientation).invert());
    tempGravityTorque.y*=.25;
    if(Math.abs(this.uprightness)<.52&&this.bodyContactTipAxis.lengthSq()<.001){
      if(tempGravityTorque.lengthSq()>.0016){
        this.bodyContactTipAxis.copy(tempGravityTorque).normalize();
      }else{
        const recovery=this.airRecoveryError();
        this.bodyContactTipAxis.set(
          clamp(recovery.pitch,-1,1),
          0,
          -clamp(recovery.roll,-1,1),
        ).normalize();
      }
    }
    if(this.bodyContactTipAxis.lengthSq()>.001){
      // Una carrocería real rara vez puede quedar perfectamente equilibrada
      // sobre el paragolpes. Conservamos el sentido inicial de la caída para
      // atravesar ese equilibrio matemático y completar el vuelco.
      tempGravityTorque.addScaledVector(this.bodyContactTipAxis,.62);
    }
    if(tempGravityTorque.lengthSq()<.0016&&Math.abs(this.uprightness)<.48){
      const recovery=this.airRecoveryError();
      tempGravityTorque.set(
        clamp(recovery.pitch,-1,1),
        0,
        -clamp(recovery.roll,-1,1),
      );
    }
    if(tempGravityTorque.lengthSq()<.000001)return;
    tempGravityTorque.normalize();
    this.angularVelocity.addScaledVector(tempGravityTorque,5.6*dt);
    const maximumSpin=3.6;
    if(this.angularVelocity.length()>maximumSpin)this.angularVelocity.setLength(maximumSpin);
  }

  damage(amount, direction, source = null) {
    if (this.disabled || this.health <= 0 || this.invulnerableTimer > 0) return false;
    if (source && source !== this) {
      this.lastDamager = source;
      const contribution = this.damageContributors.get(source) || { damage:0, remaining:8 };
      contribution.damage += amount;
      contribution.remaining = 8;
      this.damageContributors.set(source, contribution);
    }
    this.health = Math.max(0, this.health - amount);
    if (direction) this.velocity.addScaledVector(direction, amount * .07 / this.config.mass);
    if (this.health <= 0) {
      const destroyedAt = this.car.position.clone();
      this.disabled = true;
      this.respawnTimer = this.respawnDelay;
      this.velocity.set(0, 0, 0);
      this.throttle = 0;
      this.boosting = false;
      this.car.visible = false;
      this.onDestroyed(this, destroyedAt);
    }
    return true;
  }

  applyProjectileImpact(direction, position, strength = 1) {
    if(this.disabled||this.invulnerableTimer>0||!direction||!position)return false;
    const force=direction.clone();
    if(force.lengthSq()<.000001)return false;
    force.normalize();
    const collider=vehicleColliderFor(this);
    const rightX=collider.rightX;
    const rightZ=collider.rightZ;
    const forwardX=collider.forwardX;
    const forwardZ=collider.forwardZ;
    const localForceX=force.x*rightX+force.z*rightZ;
    const localForceY=force.y;
    const localForceZ=force.x*forwardX+force.z*forwardZ;
    const offsetX=position.x-collider.centerX;
    const offsetZ=position.z-collider.centerZ;
    const localOffsetX=clamp(
      (offsetX*rightX+offsetZ*rightZ)/Math.max(.05,collider.halfWidth),
      -1,
      1,
    );
    const localOffsetY=clamp(
      (position.y-collider.centerY)/Math.max(.05,(collider.top-collider.bottom)*.5),
      -1,
      1,
    );
    const localOffsetZ=clamp(
      (offsetX*forwardX+offsetZ*forwardZ)/Math.max(.05,collider.halfLength),
      -1,
      1,
    );
    const torquePitch=localOffsetY*localForceZ-localOffsetZ*localForceY;
    const torqueYaw=localOffsetZ*localForceX-localOffsetX*localForceZ;
    const torqueRoll=localOffsetX*localForceY-localOffsetY*localForceX;
    const intensity=clamp(strength,.2,1.6);
    // Los golpes ordinarios mantienen la respuesta calibrada, pero por encima
    // de 1 (nitro, explosión fuerte o choque de gran velocidad) la energía
    // angular crece de forma no lineal para poder levantar y volcar el auto.
    const angularIntensity=intensity*(1+Math.max(0,intensity-1)*2.4);

    // El componente direccional garantiza una lectura arcade clara aunque el
    // proyectil pegue cerca del centro; el torque agrega variación según el
    // punto exacto del impacto.
    const pitchImpulse=(localForceZ*.95+torquePitch*.72)*angularIntensity;
    const yawImpulse=torqueYaw*.82*angularIntensity;
    const rollImpulse=(-localForceX*3.4+torqueRoll*.95)*angularIntensity;
    this.impactPitchVelocity=clamp(
      this.impactPitchVelocity+pitchImpulse,
      -4.2,
      4.2,
    );
    this.impactYawVelocity=clamp(
      this.impactYawVelocity+yawImpulse,
      -2.8,
      2.8,
    );
    this.impactRollVelocity=clamp(
      this.impactRollVelocity+rollImpulse,
      -8.4,
      8.4,
    );
    // Un pequeño desplazamiento angular inmediato hace legible el impacto
    // incluso a 60 FPS; las velocidades anteriores continúan el balanceo.
    this.integrateLocalRotation(
      tempAngular.set(pitchImpulse*.032,yawImpulse*.018,rollImpulse*.04),
      1,
    );
    this.syncOrientationFields();
    return true;
  }

  update(dt, controls, updateVisual = true) {
    dt = Math.min(dt, .033);
    if (this.disabled) {
      this.respawnTimer = Math.max(0, this.respawnTimer - dt);
      if (this.respawnTimer <= 0) {
        const respawn = this.getRespawn?.(this);
        this.reset(respawn?.position || this.spawn, respawn?.yaw ?? this.spawnYaw, true);
      }
      return;
    }
    this.stepStartOrientation.copy(this.orientation);
    this.invulnerableTimer = Math.max(0, this.invulnerableTimer - dt);
    this.collisionGhostTimer = Math.max(0, this.collisionGhostTimer - dt);
    this.impactCooldown = Math.max(0, this.impactCooldown - dt);
    tickProjectileFireLimiter(this,dt);
    this.airDodgeWindowTimer=Math.max(0,this.airDodgeWindowTimer-dt);
    this.airDodgeSpinTimer=Math.max(0,this.airDodgeSpinTimer-dt);
    this.airDodgeImpactTimer=Math.max(0,this.airDodgeImpactTimer-dt);
    if(this.airDodgeWindowTimer<=0)this.airDodgeAvailable=false;
    this.jumpBufferTimer = controls.jump
      ? .16
      : Math.max(0, this.jumpBufferTimer - dt);
    this.damageContributors.forEach((contribution,source) => {
      contribution.remaining -= dt;
      if (contribution.remaining <= 0) this.damageContributors.delete(source);
    });
    this.throttle = clamp(controls.throttle || 0, -1, 1);
    this.handbraking = Boolean(controls.handbrake && this.wheelsGrounded);
    this.steer = THREE.MathUtils.lerp(this.steer, clamp(controls.steer || 0, -1, 1), dampFactor(10, dt));
    this.groundHeight = getGroundHeight(this.world, this.car.position);
    const obstacleSupport=this.findObstacleSupportHeight(this.car.position);
    if(obstacleSupport!==null)this.groundHeight=Math.max(this.groundHeight,obstacleSupport);
    const groundCollider=vehicleColliderFor(this);
    const nearGround = groundCollider.bottom <= this.groundHeight + .18 && this.velocity.y <= 2.5;
    if (nearGround) {
      if (!this.grounded && this.airTime > .18) {
        this.lastAirTime = this.airTime;
        this.onLand(this.airTime, Math.abs(this.velocity.y));
      }
      this.grounded = true;
      this.car.position.y += this.groundHeight-groundCollider.bottom;
      this.velocity.y = Math.max(0, this.velocity.y);
      this.airTime = 0;
      this.groundedGraceTimer = .1;
      this.resetAirDodge();
    } else {
      this.grounded = false;
      this.airTime += dt;
      this.groundedGraceTimer = Math.max(0, this.groundedGraceTimer - dt);
    }
    tempUp.set(0,1,0).applyQuaternion(this.orientation);
    this.uprightness=clamp(tempUp.y,-1,1);
    this.wheelsGrounded=this.grounded&&this.uprightness>.5;
    this.handbraking=Boolean(controls.handbrake&&this.wheelsGrounded);
    const accelerator=clamp(controls.accelerator??Math.max(0,this.throttle),0,1);
    const boostRequested=Boolean(
      Boolean(controls.boost)
      &&this.nitro>.15
      &&!controls.nitroDisabled
    );
    const cruiseTopSpeedMultiplier=this.config.cruiseTopSpeedMultiplier??1.16;
    const boostTopSpeedMultiplier=this.config.boostTopSpeedMultiplier??1.36;
    this.topSpeedMultiplier=THREE.MathUtils.lerp(
      this.topSpeedMultiplier,
      boostRequested?boostTopSpeedMultiplier:cruiseTopSpeedMultiplier,
      dampFactor(boostRequested?5.5:2.4,dt),
    );

    tempForward.set(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
    tempRight.set(Math.cos(this.yaw), 0, -Math.sin(this.yaw));
    this.forwardSpeed = this.velocity.dot(tempForward);
    this.lateralSpeed = this.velocity.dot(tempRight);
    const currentPlanarSpeed=Math.hypot(this.velocity.x,this.velocity.z);
    const lateralSlip=clamp((Math.abs(this.lateralSpeed)-.85)/3.8,0,1);
    const brakeSlip=controls.handbrake&&currentPlanarSpeed>2.2?clamp((currentPlanarSpeed-2.2)/7+.32,0,1):0;
    this.skidAmount=this.wheelsGrounded?Math.max(lateralSlip,brakeSlip):0;

    if (this.wheelsGrounded) {
      const movingDirection = Math.abs(this.forwardSpeed) > .4 ? Math.sign(this.forwardSpeed) : (this.throttle >= 0 ? 1 : -1);
      const speedRatio = clamp(Math.abs(this.forwardSpeed) / this.config.maxSpeed, 0, 1);
      const steeringPower = this.config.handling * (.25 + .75 * Math.min(speedRatio * 2.1, 1));
      const handbrakeTurn = controls.handbrake ? 1.42 : 1;
      const previousYaw=this.yaw;
      this.yaw -= this.steer * steeringPower * handbrakeTurn * movingDirection * dt;
      // El giro sobre el piso debe trasladarse completo a la orientación 3D.
      // Si sólo actualizamos `yaw`, el slerp de estabilidad que sigue absorbe
      // casi todo el giro junto con el pitch/roll que intenta enderezar.
      tempQuaternion.setFromAxisAngle(UP,normalizeAngle(this.yaw-previousYaw));
      this.orientation.premultiply(tempQuaternion).normalize();

      const accelerationScale=clamp(controls.accelerationScale ?? 1,.7,1.4);
      const acceleration = (this.throttle >= 0 ? this.config.acceleration : this.config.acceleration * .72)*accelerationScale;
      if (this.throttle !== 0) {
        const opposing = Math.sign(this.forwardSpeed) !== Math.sign(this.throttle) && Math.abs(this.forwardSpeed) > 1;
        this.velocity.addScaledVector(tempForward, this.throttle * acceleration * (opposing ? 1.7 : 1) * dt);
      }

      const traction = controls.handbrake ? 2.1 : this.config.traction;
      this.velocity.addScaledVector(tempRight, -this.lateralSpeed * Math.min(1, traction * dt));
      const rollingDrag = this.throttle === 0 ? 1.65 : .45;
      this.velocity.x *= Math.max(0, 1 - rollingDrag * dt);
      this.velocity.z *= Math.max(0, 1 - rollingDrag * dt);

      const speedScale=clamp(controls.speedScale ?? 1,.75,1.35);
      const maxForward = (this.throttle < 0 ? this.config.reverseSpeed : this.config.maxSpeed)*speedScale;
      const planarSpeed = Math.hypot(this.velocity.x, this.velocity.z);
      const topSpeed = maxForward * this.topSpeedMultiplier;
      if (planarSpeed > topSpeed) {
        const scale = topSpeed / planarSpeed;
        this.velocity.x *= scale;
        this.velocity.z *= scale;
      }

      if (this.jumpBufferTimer > 0) {
        this.grounded = false;
        this.wheelsGrounded = false;
        this.groundedGraceTimer = 0;
        this.jumpBufferTimer = 0;
        this.velocity.y = 7.8 / Math.sqrt(this.config.mass * .82);
        this.angularVelocity.x-=.24;
        this.car.position.y += .12;
        this.armAirDodge();
        this.onJump();
      }

      const impactSpin=Math.hypot(
        this.impactPitchVelocity,
        this.impactYawVelocity,
        this.impactRollVelocity,
      );
      // Un disparo estándar conserva el balanceo calibrado de ~15°. Sólo un
      // golpe realmente fuerte (choque con nitro o explosión intensa) vence
      // al estabilizador y puede iniciar un vuelco completo.
      const stabilizer=impactSpin>4.1?2.15:7.4;
      tempQuaternionB.setFromAxisAngle(UP,this.yaw);
      this.orientation.slerp(tempQuaternionB,dampFactor(stabilizer,dt));
      this.angularVelocity.multiplyScalar(Math.exp(-7.5*dt));
    } else {
      if (this.jumpBufferTimer > 0 && this.grounded) {
        const recovery=this.airRecoveryError();
        this.grounded=false;
        this.wheelsGrounded=false;
        this.groundedGraceTimer=0;
        this.jumpBufferTimer=0;
        this.velocity.y=Math.max(this.velocity.y,5.2/Math.sqrt(this.config.mass*.86));
        this.angularVelocity.x+=clamp(recovery.pitch*1.45,-3.4,3.4);
        this.angularVelocity.z-=clamp(recovery.roll*1.45,-3.4,3.4);
        if(Math.abs(recovery.pitch)+Math.abs(recovery.roll)<.25)this.angularVelocity.z+=2.4;
        this.car.position.y+=.12;
        this.armAirDodge();
        this.onJump();
      }else if (this.jumpBufferTimer > 0 && this.groundedGraceTimer > 0) {
        this.groundedGraceTimer = 0;
        this.jumpBufferTimer = 0;
        this.velocity.y = 7.8 / Math.sqrt(this.config.mass * .82);
        this.angularVelocity.x-=.24;
        this.car.position.y += .12;
        this.armAirDodge();
        this.onJump();
      }else if(this.jumpBufferTimer>0&&!this.grounded){
        this.performAirDodge(controls);
      }
      const stabilizing=Boolean(controls.stabilize);
      if(stabilizing){
        tempQuaternionB.setFromAxisAngle(UP,this.yaw);
        this.orientation.slerp(tempQuaternionB,dampFactor(6.8,dt));
        this.angularVelocity.multiplyScalar(Math.exp(-9.5*dt));
        this.bodyContactTipAxis.set(0,0,0);
      }else if(this.airDodgeSpinTimer>0){
        this.angularVelocity.multiplyScalar(Math.exp(-.18*dt));
      }else{
        const controlScale=this.grounded?.72:1;
        tempAngular.set(
          (controls.airPitch||0)*AIR_PITCH_SPEED*controlScale,
          -(controls.airYaw||0)*AIR_YAW_SPEED*controlScale,
          -(controls.airRoll||0)*AIR_ROLL_SPEED*controlScale,
        );
        const hasAirInput=tempAngular.lengthSq()>.0025;
        this.angularVelocity.lerp(
          tempAngular,
          dampFactor(hasAirInput?9.5:3.15,dt),
        );
        this.applyBodyContactGravity(dt);
      }
      this.integrateLocalRotation(this.angularVelocity,dt);
      if(this.grounded){
        this.velocity.y=Math.max(0,this.velocity.y);
        this.velocity.x*=Math.max(0,1-1.9*dt);
        this.velocity.z*=Math.max(0,1-1.9*dt);
      }else this.velocity.y -= AIR_GRAVITY * dt;
      this.velocity.multiplyScalar(1 - .045 * dt);
    }

    this.integrateLocalRotation(
      tempAngular.set(
        this.impactPitchVelocity,
        this.impactYawVelocity,
        this.impactRollVelocity,
      ),
      dt,
    );
    const angularImpactDamping=this.wheelsGrounded?4.4:this.grounded?3.1:1.85;
    const angularImpactRetention=Math.exp(-angularImpactDamping*dt);
    this.impactPitchVelocity*=angularImpactRetention;
    this.impactYawVelocity*=angularImpactRetention;
    this.impactRollVelocity*=angularImpactRetention;
    this.syncOrientationFields();

    this.boosting = boostRequested;
    if (this.boosting) {
      if(this.wheelsGrounded)tempLocal.copy(tempForward);
      else tempLocal.set(0, 0, -1).applyQuaternion(this.orientation).normalize();
      this.velocity.addScaledVector(tempLocal, this.config.boostForce * dt);
      if (!this.wheelsGrounded) this.velocity.y += 2.1 * dt;
      if(this.nitroOverchargeTimer<=0){
        this.nitro = Math.max(0, this.nitro - (29 - this.config.boost * .08) * dt);
      }else this.nitro=100;
    } else {
      const recharge = this.wheelsGrounded ? 10.5 : 3.2;
      this.nitro = Math.min(100, this.nitro + recharge * dt);
    }

    const previous = this.car.position.clone();
    this.car.position.addScaledVector(this.velocity, dt);

    const trampoline=this.world.trampolines?.find((surface)=>{
      const dx=this.car.position.x-surface.x;
      const dz=this.car.position.z-surface.z;
      return dx*dx+dz*dz<=surface.radius*surface.radius;
    });
    if(trampoline&&this.car.position.y<=trampoline.height+.28&&this.velocity.y<=1.5){
      this.car.position.y=trampoline.height+.1;
      this.velocity.y=trampoline.bounceVelocity;
      this.grounded=false;
      this.wheelsGrounded=false;
      this.angularVelocity.x-=.2;
      this.onJump();
    }

    this.resolveGroundPlaneCollision(previous,this.stepStartOrientation);
    this.resolveVerticalObstacleCollisions(previous,this.stepStartOrientation);
    this.resolveWorldCollisions(previous);

    this.distance += Math.abs(this.forwardSpeed) * dt;
    this.car.quaternion.copy(this.orientation);
    if (updateVisual) this.car.userData.updateVisual?.(this.car, this, dt);
    tickVehiclePowerups(this,dt);
  }

  findObstacleSupportHeight(position) {
    let support=null;
    const collider=vehicleColliderFor(this,position);
    for(const obstacle of this.world.obstacles){
      if(!colliderOverlapsObstacleXZ(collider,obstacle,.012,.62))continue;
      const top=obstacle.maxY;
      if(collider.bottom<top-.38||collider.bottom>top+.34)continue;
      if(support===null||top>support)support=top;
    }
    return support;
  }

  findShadowSupportHeight(position){
    let support=getGroundHeight(this.world,position);
    for(const obstacle of this.world.obstacles){
      if(!obstacleContainsPointXZ(position,obstacle,0))continue;
      const top=obstacle.maxY;
      if(top>position.y+.08||top<=support)continue;
      support=top;
    }
    return support;
  }

  resolveGroundPlaneCollision(previous,previousOrientation=this.orientation) {
    // Un salto que ya se aleja del piso no debe volver a declararse apoyado
    // sólo porque una esquina rotó cerca de la superficie durante ese tick.
    if(this.velocity.y>2.5)return false;
    const currentCollider=vehicleColliderFor(this);
    const previousCollider=vehicleColliderFor(this,previous,null,previousOrientation);
    const currentGround=getGroundHeight(this.world,this.car.position);
    const previousGround=getGroundHeight(this.world,previous);
    const crossedSurface=previousCollider.bottom>=previousGround-.08
      &&currentCollider.bottom<=currentGround+.03;
    const shallowPenetration=currentCollider.bottom<currentGround
      &&currentCollider.top>currentGround
      &&currentCollider.centerY>=currentGround-.08;
    if(!crossedSurface&&!shallowPenetration)return false;
    const impactSpeed=Math.abs(Math.min(0,this.velocity.y));
    if(!this.grounded&&this.airTime>.18){
      this.lastAirTime=this.airTime;
      this.onLand(this.airTime,impactSpeed);
    }
    this.car.position.y+=currentGround-currentCollider.bottom;
    this.velocity.y=Math.max(0,this.velocity.y);
    this.groundHeight=currentGround;
    this.grounded=true;
    tempUp.set(0,1,0).applyQuaternion(this.orientation);
    this.uprightness=clamp(tempUp.y,-1,1);
    this.wheelsGrounded=this.uprightness>.5;
    this.airTime=0;
    this.groundedGraceTimer=.1;
    this.resetAirDodge();
    return true;
  }

  resolveVerticalObstacleCollisions(previous,previousOrientation=this.orientation) {
    const previousCollider=vehicleColliderFor(this,previous,null,previousOrientation);
    const currentCollider=vehicleColliderFor(this);
    const previousBottom=previousCollider.bottom;
    const currentBottom=currentCollider.bottom;
    const previousTop=previousCollider.top;
    const currentTop=currentCollider.top;
    if(this.velocity.y<=0){
      let landingTop=null;
      for(const obstacle of this.world.obstacles){
        const overlapsSurface=colliderOverlapsObstacleXZ(currentCollider,obstacle,.015,.68)
          ||colliderOverlapsObstacleXZ(previousCollider,obstacle,.015,.68);
        if(!overlapsSurface)continue;
        const top=obstacle.maxY;
        const crossedSurface=previousBottom>=top-.12&&currentBottom<=top+.12;
        const shallowPenetration=currentBottom<top
          &&currentTop>top
          &&currentBottom>=top-Math.min(.48,(currentTop-currentBottom)*.58)
          &&currentCollider.centerY>=top-.06;
        if(!crossedSurface&&!shallowPenetration)continue;
        if(landingTop===null||top>landingTop)landingTop=top;
      }
      if(landingTop!==null){
        const impactSpeed=Math.abs(this.velocity.y);
        if(!this.grounded&&this.airTime>.18){
          this.lastAirTime=this.airTime;
          this.onLand(this.airTime,impactSpeed);
        }
        this.car.position.y+=landingTop-currentCollider.bottom;
        this.velocity.y=0;
        this.groundHeight=landingTop;
        this.grounded=true;
        tempUp.set(0,1,0).applyQuaternion(this.orientation);
        this.uprightness=clamp(tempUp.y,-1,1);
        this.wheelsGrounded=this.uprightness>.5;
        this.airTime=0;
        this.groundedGraceTimer=.1;
        this.resetAirDodge();
        return;
      }
    }

    if(this.velocity.y>0){
      let ceilingBottom=null;
      for(const obstacle of this.world.obstacles){
        if(!colliderOverlapsObstacleXZ(currentCollider,obstacle,.015,.82)
          &&!colliderOverlapsObstacleXZ(previousCollider,obstacle,.015,.82))continue;
        const bottom=obstacle.minY;
        if(previousTop>bottom+.1||currentTop<bottom-.12)continue;
        if(ceilingBottom===null||bottom<ceilingBottom)ceilingBottom=bottom;
      }
      if(ceilingBottom!==null){
        this.car.position.y+=ceilingBottom-currentCollider.top-.025;
        this.velocity.y=-Math.max(1.1,this.velocity.y*.32);
        this.grounded=false;
      }
    }
  }

  resolveWorldCollisions(previous) {
    const initialCollider=vehicleColliderFor(this);
    const radius = Math.max(
      this.car.userData.radius*.9,
      Math.min(initialCollider.halfWidth,initialCollider.halfLength)*.84,
    );
    const collisionHeight=initialCollider.top-initialCollider.bottom;
    const halfWidth = this.world.width / 2 - .8;
    const halfDepth = this.world.depth / 2 - .8;
    let normal = null;
    if (this.car.position.x < -halfWidth) { this.car.position.x = -halfWidth; normal = new THREE.Vector3(1,0,0); }
    if (this.car.position.x > halfWidth) { this.car.position.x = halfWidth; normal = new THREE.Vector3(-1,0,0); }
    if (this.car.position.z < -halfDepth) { this.car.position.z = -halfDepth; normal = new THREE.Vector3(0,0,1); }
    if (this.car.position.z > halfDepth) { this.car.position.z = halfDepth; normal = new THREE.Vector3(0,0,-1); }

    const previousCollider=vehicleColliderFor(this,previous);
    const currentCollider=vehicleColliderFor(this);
    const sweepStart=new THREE.Vector3(
      previousCollider.centerX,
      previousCollider.bottom,
      previousCollider.centerZ,
    );
    const sweepEnd=new THREE.Vector3(
      currentCollider.centerX,
      currentCollider.bottom,
      currentCollider.centerZ,
    );
    let earliestHit=null;
    for(const obstacle of this.world.obstacles){
      if(obstacle.vehicleSideCollision===false)continue;
      const hit=sweepCircleObstacle(sweepStart,sweepEnd,radius,collisionHeight,obstacle);
      if(hit&&(!earliestHit||hit.time<earliestHit.time))earliestHit=hit;
    }
    if(earliestHit){
      // Conservamos el movimiento vertical del tick: la barrida sólo evita
      // atravesar el lateral del objeto. El resolvedor vertical sigue siendo
      // quien permite aterrizar encima o pasar limpiamente por arriba/abajo.
      const resolvedCenterX=THREE.MathUtils.lerp(sweepStart.x,sweepEnd.x,earliestHit.time)
        +earliestHit.normal.x*.014;
      const resolvedCenterZ=THREE.MathUtils.lerp(sweepStart.z,sweepEnd.z,earliestHit.time)
        +earliestHit.normal.z*.014;
      this.car.position.x+=resolvedCenterX-currentCollider.centerX;
      this.car.position.z+=resolvedCenterZ-currentCollider.centerZ;
      normal=earliestHit.normal;
    }

    // Una sola pasada no alcanza en las esquinas ni entre colliders que se
    // solapan: corregir un mueble puede empujar el auto dentro del siguiente.
    // Las pasadas extra convergen a una posición libre sin alterar el collider.
    for(let pass=0;pass<3;pass+=1){
      let corrected=false;
      for (const obstacle of this.world.obstacles) {
        if(obstacle.vehicleSideCollision===false)continue;
        const collider=vehicleColliderFor(this);
        // Un auto apoyado sobre el objeto no debe entrar simultáneamente en
        // la resolución lateral por una diferencia mínima de redondeo.
        if (collider.top <= obstacle.minY+.02 || collider.bottom >= obstacle.maxY-.035) continue;
        const colliderCenter=tempLocal.set(collider.centerX,this.car.position.y,collider.centerZ);
        const collision = resolveCircleObstacle(colliderCenter, radius, obstacle);
        if (!collision) continue;
        this.car.position.addScaledVector(collision.normal, collision.depth + .012);
        normal = collision.normal;
        corrected=true;
      }
      if(!corrected)break;
    }

    if (normal) {
      const intoWall = this.velocity.dot(normal);
      const impact = Math.abs(intoWall);
      if (intoWall < 0) this.velocity.addScaledVector(normal, -intoWall * 1.55);
      this.velocity.x *= .72;
      this.velocity.z *= .72;
      if (impact > 3 && this.impactCooldown <= 0) {
        this.impactCooldown = .18;
        this.applyProjectileImpact(
          normal,
          this.car.position,
          clamp(impact/11,.28,1.35),
        );
        this.onImpact(impact, this.car.position.clone(), normal);
      }
      if (!Number.isFinite(this.car.position.x)) this.car.position.copy(previous);
    }

    if (this.car.position.y < -5) this.reset();
  }
}

export function resolveVehicleContacts(vehicles) {
  const contacts = [];
  for (let i = 0; i < vehicles.length; i += 1) {
    for (let j = i + 1; j < vehicles.length; j += 1) {
      const a = vehicles[i];
      const b = vehicles[j];
      if (a.disabled || b.disabled || a.collisionGhostTimer>0 || b.collisionGhostTimer>0) continue;
      const collision=vehicleColliderContact(a,b);
      if(!collision)continue;
      const {normal,overlap}=collision;
      const totalMass = a.config.mass + b.config.mass;
      a.car.position.addScaledVector(normal, -overlap * b.config.mass / totalMass);
      b.car.position.addScaledVector(normal, overlap * a.config.mass / totalMass);
      const relative = tempRight.subVectors(b.velocity, a.velocity).dot(normal);
      contacts.push({
        a,
        b,
        closingSpeed: Math.max(0, -relative),
        overlap,
        position:collision.position,
      });
      if (relative < 0) {
        const impulse = -(1.35) * relative / (1 / a.config.mass + 1 / b.config.mass);
        const aShieldScale=a.powerupShieldTimer>0 ? .28 : 1;
        const bShieldScale=b.powerupShieldTimer>0 ? .28 : 1;
        a.velocity.addScaledVector(normal, -impulse / a.config.mass*aShieldScale);
        b.velocity.addScaledVector(normal, impulse / b.config.mass*bShieldScale);
        const angularStrength=clamp(Math.abs(relative)/10,.2,1.4);
        a.applyProjectileImpact(normal.clone().negate(),collision.position,angularStrength*aShieldScale);
        b.applyProjectileImpact(normal,collision.position,angularStrength*bShieldScale);
        if (Math.abs(relative) > 5) {
          a.onImpact(Math.abs(relative), a.car.position.clone(), normal.clone().negate());
          b.onImpact(Math.abs(relative), b.car.position.clone(), normal.clone());
        }
      }
    }
  }
  return contacts;
}

// Predicción de contacto para el vehículo controlado localmente. El servidor
// sigue resolviendo el choque real; esta variante sólo mueve y frena al auto
// propio para que la respuesta visual no espere un viaje completo de red.
// La barrida muestrea el OBB durante el tick para evitar atravesar rivales a
// alta velocidad sin volver a introducir el viejo collider circular.
export function resolveLocalVehicleContacts(local,vehicles,previousPosition,dt=1/30) {
  const contacts=[];
  const previous=previousPosition||local.car.position;
  for(const remote of vehicles){
    if(remote===local||local.disabled||remote.disabled)continue;
    const remotePrevious=remote.car.position.clone().addScaledVector(remote.velocity,-dt);
    let localAtContact=local.car.position.clone();
    let remoteAtContact=remote.car.position.clone();
    let collision=vehicleColliderContact(local,remote);
    if(!collision){
      const relativeStart=tempContactStart.subVectors(previous,remotePrevious);
      const relativeEnd=tempContactEnd.subVectors(local.car.position,remote.car.position);
      const relativeTravel=tempContactMotion.subVectors(relativeEnd,relativeStart).length();
      const localCollider=vehicleColliderFor(local);
      const remoteCollider=vehicleColliderFor(remote);
      const minimumExtent=Math.max(.08,Math.min(
        localCollider.halfWidth,
        localCollider.halfLength,
        remoteCollider.halfWidth,
        remoteCollider.halfLength,
      ));
      const steps=THREE.MathUtils.clamp(
        Math.ceil(relativeTravel/(minimumExtent*.35)),
        2,
        16,
      );
      for(let step=0;step<=steps;step+=1){
        const alpha=step/steps;
        const sampledLocal=new THREE.Vector3().lerpVectors(previous,local.car.position,alpha);
        const sampledRemote=new THREE.Vector3().lerpVectors(remotePrevious,remote.car.position,alpha);
        const sampledCollision=vehicleColliderContact(
          local,
          remote,
          sampledLocal,
          sampledRemote,
        );
        if(!sampledCollision)continue;
        localAtContact=sampledLocal;
        remoteAtContact=sampledRemote;
        collision=sampledCollision;
        break;
      }
    }
    if(!collision)continue;

    const {normal,overlap}=collision;
    local.car.position.x=localAtContact.x-normal.x*(overlap+.012);
    local.car.position.z=localAtContact.z-normal.z*(overlap+.012);

    const relativeSpeed=tempRight.subVectors(remote.velocity,local.velocity).dot(normal);
    const closingSpeed=Math.max(0,-relativeSpeed);
    const shieldScale=local.powerupShieldTimer>0 ? .28 : 1;
    if(relativeSpeed<0)local.velocity.addScaledVector(normal,relativeSpeed*1.08*shieldScale);
    if(closingSpeed>2.2)local.applyProjectileImpact(
      normal.clone().negate(),
      collision.position,
      clamp(closingSpeed/10,.2,1.25)*shieldScale,
    );
    contacts.push({
      a:local,
      b:remote,
      closingSpeed,
      overlap,
      position:localAtContact.clone().lerp(remoteAtContact,.5),
    });
    if(closingSpeed>5&&local.impactCooldown<=0){
      local.impactCooldown=.18;
      local.onImpact(closingSpeed,local.car.position.clone(),normal.clone().negate());
    }
  }
  return contacts;
}

export function normalizeAngle(angle) {
  return Math.atan2(Math.sin(angle), Math.cos(angle));
}
